export type RoleBadgeVariant =
  | "soft-primary"
  | "soft-success"
  | "soft-warning"
  | "soft-info"
  | "soft-error";

export function normalizeRole(value: unknown): string {
  if (typeof value !== "string") return "";
  const compact = value.replace(/[\s_-]+/g, "").toLowerCase();
  if (compact === "storemanager") return "StoreManager";
  if (compact === "admin") return "Admin";
  if (compact === "merchant") return "Merchant";
  if (compact === "user") return "User";
  return value.trim();
}

export function getRoleBadgeVariant(role: unknown): RoleBadgeVariant {
  const normalized = normalizeRole(role);
  if (normalized === "Admin") return "soft-error";
  if (normalized === "StoreManager") return "soft-warning";
  if (normalized === "Merchant") return "soft-primary";
  if (normalized === "User") return "soft-info";
  return "soft-info";
}

export function isAdminRole(role: string | undefined): boolean {
  return role === "Admin";
}

export function isStoreManagerRole(role: string | undefined): boolean {
  return role === "StoreManager";
}

export function isMerchantRole(role: string | undefined): boolean {
  return !!role && !isAdminRole(role) && !isStoreManagerRole(role);
}

/** Mirrors the backend's fixed catalog (models/user.js's SECONDARY_ROLES) — only meaningful
 * on role: 'Admin' users. 'Admin' here is the privileged tier: the one secondary role that can
 * itself assign/revoke secondary roles on others. */
export const SECONDARY_ROLES = [
  "Admin",
  "DriverManager",
  "OperationsManager",
  "OperationsSupport",
  "CustomerSupport",
  "DriverSupport",
  "FinanceManager",
  "MerchantSupport",
] as const;

export type SecondaryRole = (typeof SECONDARY_ROLES)[number];

export function getSecondaryRoleBadgeVariant(secondaryRole: unknown): RoleBadgeVariant {
  switch (secondaryRole) {
    case "Admin":
      return "soft-error";
    case "DriverManager":
      return "soft-primary";
    case "OperationsManager":
      return "soft-warning";
    case "FinanceManager":
      return "soft-success";
    default:
      return "soft-info";
  }
}

/** The actor's OWN secondary role is 'Admin' — the gate for the "Assign Role"/"Assign Manager"
 * actions, separate from (and in addition to) plain role: 'Admin'. */
export function isAdminSecondaryRole(secondaryRole: string | undefined): boolean {
  return secondaryRole === "Admin";
}

export interface OrderRoleCapabilities {
  canCreateOrder: boolean;
  canUpdateOrder: boolean;
  canRunBulkActions: boolean;
  canFilterByMerchant: boolean;
  /** Compose an ORDER-level comment (orderId only, no shipmentId/waybill) —
   *  the Order Details page's own composer. Admin-only, per direct
   *  instruction. Reading is open to every role — the backend scopes and
   *  redacts. */
  canManageComments: boolean;
  /** Compose a SHIPMENT-level comment (shipmentId/waybill present) — the
   *  Shipment page's own composer. Also Admin-only, per direct instruction
   *  (a backend change to match — addShipmentCommentV4's own ALLOWED_ROLES
   *  still lists Merchant/StoreManager/User at the time of writing — is in
   *  progress separately). Kept as its own flag since the two endpoints'
   *  gates have diverged before and may again. */
  canManageShipmentComments: boolean;
  /** Edit an EXISTING comment (any row, not just one's own, order- or
   *  shipment-level) — Admin only. editCommentV4 is wrapped in requireAdmin
   *  on the backend regardless of who can compose. */
  canEditComments: boolean;
}

export function getOrderRoleCapabilities(role: string | undefined): OrderRoleCapabilities {
  const isAdmin = isAdminRole(role);
  const isStoreManager = isStoreManagerRole(role);
  const isMerchant = isMerchantRole(role);

  return {
    canCreateOrder: isStoreManager || isMerchant,
    canUpdateOrder: isStoreManager || isMerchant,
    canRunBulkActions: isStoreManager || isMerchant,
    canFilterByMerchant: isAdmin,
    canManageComments: isAdmin,
    canManageShipmentComments: isAdmin,
    canEditComments: isAdmin,
  };
}

export interface PackageTypeRoleCapabilities {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /** Admin needs a merchant picker since the list isn't auto-scoped server-side. */
  canFilterByMerchant: boolean;
}

export function getPackageTypeRoleCapabilities(
  role: string | undefined
): PackageTypeRoleCapabilities {
  const isAdmin = isAdminRole(role);
  return {
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canFilterByMerchant: isAdmin,
  };
}

export interface AddressValidationRoleCapabilities {
  /** The feature is unavailable to 'User'/'Store' roles — the backend now
   *  rejects it for them entirely, so the UI shouldn't offer an entry point. */
  canAccess: boolean;
  // 'Merchant' and 'StoreManager' keep full backend/route access (canAccess
  // stays true) — only the sidebar entry point is hidden for them, by direct
  // instruction. Not named canAccess/hidden alongside it so a route guard
  // never mistakes this for an access restriction.
  showInSidebar: boolean;
  canResolve: boolean;
  canRevalidate: boolean;
  canFilterByMerchant: boolean;
}

// Every role can resolve a shipment in its own scope (that's the whole point
// of the queue); only Admin gets the more global/destructive action.
// ("Dismiss" used to be a second Admin-only action here, but
// resolveShipmentAddress.js dropped it entirely — Accept Suggestion / Keep
// Original / Save Edits is the full decision flow now.)
export function getAddressValidationCapabilities(
  role: string | undefined
): AddressValidationRoleCapabilities {
  const isAdmin = isAdminRole(role);
  const canAccess = role !== "User" && role !== "Store";
  return {
    canAccess,
    showInSidebar: canAccess && role !== "Merchant" && role !== "StoreManager",
    canResolve: true,
    canRevalidate: isAdmin,
    canFilterByMerchant: isAdmin,
  };
}

export interface ProblemSolveRoleCapabilities {
  // listProblemSolveV4's own VIEW_ROLES (shipments) and manifestAccess.js's
  // buildScopedManifestQuery (manifests) both allow 'User' — locked to their
  // own merchant, same as the orders listview, no assignment check —
  // verified against the actual controllers, not just the scope doc.
  // 'Store' stays excluded from both.
  canAccess: boolean;
  // Unlike address validation, there's no further hiding for Merchant/
  // StoreManager here — every accessible role gets the nav entry.
  showInSidebar: boolean;
  // problemSolveAccess.js's own shared ALLOWED_ROLES — which gates
  // getProblemSolveShipmentV4 (the shipment workspace) — now also includes
  // 'User', given "the SAME standing as Merchant/StoreManager: viewing/
  // commenting" (per the backend's own comment there). addShipmentCommentV4
  // and bulkRequestReattemptV4 were broadened the same way, so there's
  // nothing extra to gate for commenting specifically. Admin-only actions
  // (Reattempt/RTO/Awaiting Feedback/Reassign Manager) stay on canAction
  // below, untouched. Currently the same role set as canAccess — kept as
  // its own flag since the two endpoints it tracks are separate from the
  // VIEW_ROLES ones and have diverged before.
  canViewShipmentDetail: boolean;
  // getProblemSolveCountV4's own COUNT_ROLES now also includes 'User' (same
  // role set as canAccess) — kept as its own flag since this endpoint is
  // separate from the others above and has diverged from them before.
  canSeeNavBadgeCount: boolean;
  // Reattempt / Return to origin / Awaiting feedback — Admin only; Merchant/
  // StoreManager/User get a read-only queue and workspace.
  canAction: boolean;
  canFilterByMerchant: boolean;
}

export function getProblemSolveCapabilities(
  role: string | undefined
): ProblemSolveRoleCapabilities {
  const isAdmin = isAdminRole(role);
  const canAccess = role !== "Store";
  const canViewShipmentDetail = canAccess;
  const canSeeNavBadgeCount = canAccess;
  return {
    canAccess,
    showInSidebar: canAccess,
    canViewShipmentDetail,
    canSeeNavBadgeCount,
    canAction: isAdmin,
    canFilterByMerchant: isAdmin,
  };
}
